import { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, Edit2, Trash2, Eye, EyeOff, X, Bold, Italic, List, Heading, Link2, Code, Type } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';
const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Getting Started', 'Using the Extension', 'PI Score and Analysis',
  'Rewrite and Tones', 'Prompt Copilot', 'Content Generator',
  'Account and Privacy', 'Troubleshooting'
];

function MarkdownPreview({ content }) {
  const lines = (content || '').split('\n');
  return (
    <div className="prose prose-invert prose-sm max-w-none text-white/70 space-y-2">
      {lines.map((line, idx) => {
        const key = `line-${idx}-${line.slice(0, 20)}`;
        if (line.startsWith('### ')) return <h3 key={key} className="text-white text-base font-semibold mt-3">{line.slice(4)}</h3>;
        if (line.startsWith('## ')) return <h2 key={key} className="text-white text-lg font-bold mt-4">{line.slice(3)}</h2>;
        if (line.startsWith('# ')) return <h1 key={key} className="text-white text-xl font-bold mt-4">{line.slice(2)}</h1>;
        if (line.startsWith('- ')) return <li key={key} className="ml-4 list-disc text-white/60">{line.slice(2)}</li>;
        if (line.startsWith('```')) return <div key={key} className="bg-white/5 rounded-lg p-3 font-mono text-xs text-cyan-400" />;
        if (line.trim() === '') return <br key={key} />;
        const parts = [];
        const regex = /(\*\*(.*?)\*\*)|(\*(.*?)\*)|(`(.*?)`)|([^*`]+)/g;
        let match;
        let partIdx = 0;
        while ((match = regex.exec(line)) !== null) {
          if (match[2]) parts.push(<strong key={partIdx++} className="text-white">{match[2]}</strong>);
          else if (match[4]) parts.push(<em key={partIdx++}>{match[4]}</em>);
          else if (match[6]) parts.push(<code key={partIdx++} className="bg-white/10 px-1 rounded text-cyan-400">{match[6]}</code>);
          else if (match[7]) parts.push(<span key={partIdx++}>{match[7]}</span>);
        }
        return <p key={key} className="text-white/60">{parts.length > 0 ? parts : line}</p>;
      })}
    </div>
  );
}

export default function AdminKnowledgeBase() {
  const [articles, setArticles] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', category: CATEGORIES[0], content: '', is_published: false });
  const [previewMode, setPreviewMode] = useState(false);
  const [filterCat, setFilterCat] = useState('all');

  const fetchArticles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/knowledge-base`, { credentials: 'include' });
      if (res.ok) setArticles((await res.json()).articles || []);
    } catch (err) { logger.error("Operation failed:", err); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const saveArticle = async () => {
    const method = editing ? 'PUT' : 'POST';
    const url = editing ? `${API}/api/admin/knowledge-base/${editing}` : `${API}/api/admin/knowledge-base`;
    try {
      await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success(editing ? 'Article updated' : 'Article created');
      setShowEditor(false); setEditing(null); setForm({ title: '', category: CATEGORIES[0], content: '', is_published: false });
      setPreviewMode(false);
      fetchArticles();
    } catch (e) { toast.error('Save failed'); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await fetch(`${API}/api/admin/knowledge-base/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Deleted');
    fetchArticles();
  };

  const editArticle = (a) => {
    setEditing(a.id);
    setForm({ title: a.title, category: a.category, content: a.content, is_published: a.is_published });
    setShowEditor(true); setPreviewMode(false);
  };

  const insertMarkdown = (prefix, suffix = '') => {
    const textarea = document.getElementById('kb-editor');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = form.content.substring(start, end);
    const replacement = prefix + (selected || 'text') + suffix;
    const newContent = form.content.substring(0, start) + replacement + form.content.substring(end);
    setForm({ ...form, content: newContent });
  };

  const filtered = filterCat === 'all' ? articles : articles.filter(a => a.category === filterCat);

  return (
    <div data-testid="admin-kb" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-space">Knowledge Base</h1>
          <p className="text-white/40 text-sm mt-1">{articles.length} articles</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ title: '', category: CATEGORIES[0], content: '', is_published: false }); setShowEditor(true); setPreviewMode(false); }}
          className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white border-0 rounded-full" data-testid="new-article-btn">
          <Plus className="w-4 h-4 mr-2" /> New Article
        </Button>
      </div>

      {/* Editor */}
      {showEditor && (
        <div className="bg-[#0E1830] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="kb-editor-panel">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold">{editing ? 'Edit' : 'Create'} Article</h2>
            <button onClick={() => { setShowEditor(false); setPreviewMode(false); }} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Article title"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50" data-testid="article-title" />

          <div className="flex gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none flex-1">
              {CATEGORIES.map(c => <option key={c} value={c} className="bg-[#0E1830]">{c}</option>)}
            </select>
            <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                className="accent-cyan-500" />
              <span className="text-white/60 text-sm">Published</span>
            </label>
          </div>

          {/* Formatting Toolbar */}
          <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1.5 border border-white/10" data-testid="editor-toolbar">
            <button onClick={() => insertMarkdown('**', '**')} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10" title="Bold">
              <Bold className="w-4 h-4" />
            </button>
            <button onClick={() => insertMarkdown('*', '*')} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10" title="Italic">
              <Italic className="w-4 h-4" />
            </button>
            <button onClick={() => insertMarkdown('## ')} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10" title="Heading">
              <Heading className="w-4 h-4" />
            </button>
            <button onClick={() => insertMarkdown('- ')} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10" title="List">
              <List className="w-4 h-4" />
            </button>
            <button onClick={() => insertMarkdown('[', '](url)')} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10" title="Link">
              <Link2 className="w-4 h-4" />
            </button>
            <button onClick={() => insertMarkdown('`', '`')} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10" title="Code">
              <Code className="w-4 h-4" />
            </button>
            <div className="flex-1" />
            <button onClick={() => setPreviewMode(!previewMode)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${previewMode ? 'bg-cyan-500/20 text-cyan-400' : 'text-white/40 hover:text-white'}`}>
              {previewMode ? 'Edit' : 'Preview'}
            </button>
          </div>

          {/* Editor / Preview */}
          {previewMode ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 min-h-[300px]">
              <MarkdownPreview content={form.content} />
            </div>
          ) : (
            <textarea id="kb-editor" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write your article in Markdown...&#10;&#10;Use **bold**, *italic*, ## headings, - lists, `code`"
              rows={14} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white font-mono resize-none focus:outline-none focus:border-cyan-500/50" data-testid="article-content" />
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setShowEditor(false); setPreviewMode(false); }}
              className="border-white/10 text-white/50">Cancel</Button>
            <Button onClick={saveArticle} disabled={!form.title || !form.content}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white border-0" data-testid="save-article-btn">
              {editing ? 'Update' : 'Create'} Article
            </Button>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button onClick={() => setFilterCat('all')}
          className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${filterCat === 'all' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-white/40 border border-white/10'}`}>
          All ({articles.length})
        </button>
        {CATEGORIES.map(c => {
          const count = articles.filter(a => a.category === c).length;
          return (
            <button key={c} onClick={() => setFilterCat(c)}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${filterCat === c ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-white/40 border border-white/10'}`}>
              {c} ({count})
            </button>
          );
        })}
      </div>

      {/* Articles List */}
      <div className="space-y-2">
        {filtered.map(a => (
          <div key={a.id} className="bg-[#0E1830] border border-white/10 rounded-xl p-4 flex items-center justify-between group hover:border-white/20 transition-colors" data-testid={`article-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium text-sm truncate">{a.title}</span>
                {a.is_published ? <Eye className="w-3 h-3 text-emerald-400 shrink-0" /> : <EyeOff className="w-3 h-3 text-white/20 shrink-0" />}
              </div>
              <div className="text-white/30 text-xs mt-1 flex items-center gap-3">
                <span className="px-2 py-0.5 bg-white/5 rounded-full">{a.category}</span>
                <span>{a.view_count || 0} views</span>
                <span>{a.helpful_votes || 0} helpful</span>
              </div>
            </div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => editArticle(a)} className="p-2 text-white/30 hover:text-cyan-400 rounded-lg hover:bg-white/5">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => deleteArticle(a.id)} className="p-2 text-white/30 hover:text-red-400 rounded-lg hover:bg-white/5">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-white/30 text-center py-8">No articles {filterCat !== 'all' ? `in ${filterCat}` : 'yet'}</p>}
      </div>
    </div>
  );
}
